// frontend/src/api/stats.ts
import { ApiError } from './auth';
import { API_BASE_URL } from '@/config/apiConfig';
import { createApiClient, isAxiosError, isCancel } from './client';
// --- MODIFIED: Import new schemas ---
import {
    PlayerSeasonStatsRead,
    PlayerStatsEntryForList, // Used by PublicPlayerStatsPage
    PlayerGameLogResponse,    // New
    PlayerStatCompositionResponse, // New
    GameLogLeaderboardEntry,
    AvailableHistoricalSeason,
    ResearchSeasonOption,
    PlayerSeasonSummary,
} from './schemas'; // Assuming all schemas are in schemas.ts
// --- END MODIFIED ---
import * as schemas from './schemas';
import type { PlayerMatchMoments } from '@/utils/momentReplay';


const statsApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

export interface PlayerSeasonRank {
  overall_rank: number | null;
  position_rank: number | null;
  position: string | null;
  games_appeared: number;
}

export const fetchPlayerSeasonRank = async (
  playerId: number,
  competitionIds: number[],
  signal?: AbortSignal,
): Promise<PlayerSeasonRank> => {
  const params = new URLSearchParams();
  for (const competitionId of competitionIds) params.append('competition_ids', String(competitionId));
  const url = `${API_BASE_URL}/stats/players/${playerId}/season-rank?${params}`;
  try {
    const response = await statsApiClient.get<PlayerSeasonRank>(url, { signal });
    return response.data;
  } catch (error: unknown) {
    if (isCancel(error)) throw error;
    const status = isAxiosError(error) ? error.response?.status : undefined;
    throw new ApiError('Unable to load current-season player ranks.', status, { cause: error });
  }
};

export const fetchSinglePlayerSeasonStats = async (
  playerId: number,
  token: string | null
): Promise<PlayerSeasonStatsRead | null> => {
  if (!playerId || playerId <= 0) throw new Error("Invalid Player ID.");
  const url = `${API_BASE_URL}/stats/player/${playerId}`;
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    const response = await statsApiClient.get<PlayerSeasonStatsRead>(url, { headers });
    return response.data;
  } catch (error: unknown) {
    let errorMessage = `Failed to fetch stats for player ${playerId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      if (status === 404) return null;
      errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error instanceof Error && error.message) {
      errorMessage = error.message;
    }
    console.error(`[API Stats] Error fetching single PSS for P:${playerId}:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

export const fetchPlayerStatsForCompetition = async (
  playerId: number,
  competitionId: number,
  token?: string | null
): Promise<PlayerSeasonStatsRead | null> => {
  if (!playerId || !competitionId) throw new Error("Player ID and Competition ID required.");
  const url = `${API_BASE_URL}/stats/player/${playerId}/competition/${competitionId}`;
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    const response = await statsApiClient.get<PlayerSeasonStatsRead>(url, { headers });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch stats for P:${playerId} C:${competitionId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      if (status === 404) return null;
      errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API Stats] Error fetching PSS for P:${playerId} C:${competitionId}:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};

// --- Fetch distinct teams for a competition's active season ---
export interface CompetitionTeam { team_id: number; team_name: string; }
export const fetchTeamsForCompetition = async (competitionDbId: number): Promise<CompetitionTeam[]> => {
    const url = `${API_BASE_URL}/stats/competitions/${competitionDbId}/active-season/teams`;
    const response = await statsApiClient.get<CompetitionTeam[]>(url);
    return response.data;
};

// --- API FUNCTION for PublicPlayerStatsPage (Research Endpoint) ---
export const fetchPlayersStatsForCompetitionActiveSeason = async (
    competitionDbId: number,
    filters: {
        position?: string | null;
        team_id?: number | null;
        search?: string | null; // The search filter from the hook
        sort_by?: string;
        order?: 'asc' | 'desc';
        skip?: number;
        limit?: number;
        // Draft-research surfaces: restrict to current pool members (draftable).
        pool_members_only?: boolean;
        fallback_to_prior?: boolean;
    },
    token?: string | null
): Promise<PlayerStatsEntryForList[]> => {
    if (!competitionDbId) throw new Error("Competition DB ID is required.");
    
    // Create a new URLSearchParams object for building the query string
    const queryParams = new URLSearchParams();

    // Append all provided filters to the search params
    if (filters.position) queryParams.append('position', filters.position);
    if (filters.team_id !== null && filters.team_id !== undefined) queryParams.append('team_id', String(filters.team_id));
    
    // --- THIS IS THE CRITICAL FIX ---
    if (filters.search) {
        queryParams.append('search', filters.search);
    }
    // --- END OF CRITICAL FIX ---

    if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
    if (filters.order) queryParams.append('order', filters.order);
    if (filters.skip !== null && filters.skip !== undefined) queryParams.append('skip', String(filters.skip));
    if (filters.limit !== null && filters.limit !== undefined) queryParams.append('limit', String(filters.limit));
    if (filters.pool_members_only) queryParams.append('pool_members_only', 'true');
    if (filters.fallback_to_prior !== undefined) queryParams.append('fallback_to_prior', String(filters.fallback_to_prior));

    const url = `${API_BASE_URL}/stats/competitions/${competitionDbId}/active-season/players?${queryParams.toString()}`;
    const headers: { [key: string]: string } = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
        const response = await statsApiClient.get<PlayerStatsEntryForList[]>(url, { headers });
        return response.data;
    } catch (error: any) {
        let errorMessage = `Failed to fetch player stats for competition ${competitionDbId}.`;
        // ... (standard error handling)
        throw new ApiError(errorMessage, error?.response?.status);
    }
};


// <<< --- START NEW API FUNCTION for Game Log --- >>>
export const fetchPlayerActiveSeasonGameLog = async (
  playerId: number,
  competitionDbId: number,
  token?: string | null,
  seasonIdentifier?: string | null, // archived season, e.g. 'La Liga 2025/2026'; omit for active
): Promise<PlayerGameLogResponse> => {
  if (!playerId || !competitionDbId) {
    throw new ApiError("Player ID and Competition DB ID are required.", 400);
  }
  const seasonQuery = seasonIdentifier ? `?season_identifier=${encodeURIComponent(seasonIdentifier)}` : '';
  const url = `${API_BASE_URL}/stats/players/${playerId}/competitions/${competitionDbId}/active-season/game-log${seasonQuery}`;
  console.log(`[API Stats] Fetching game log for P:${playerId}, CompDB:${competitionDbId}, Season:${seasonIdentifier ?? 'active'}`);
  
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await statsApiClient.get<PlayerGameLogResponse>(url, { headers });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch game log for player ${playerId}, competition ${competitionDbId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API Stats] Error fetching game log for P:${playerId} CompDB:${competitionDbId}:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};
// <<< --- END NEW API FUNCTION --- >>>


// <<< --- START NEW API FUNCTION for Stat Composition --- >>>
export const fetchPlayerActiveSeasonStatComposition = async (
  playerId: number,
  competitionDbId: number,
  token?: string | null
): Promise<PlayerStatCompositionResponse> => {
  if (!playerId || !competitionDbId) {
    throw new ApiError("Player ID and Competition DB ID are required.", 400);
  }
  const url = `${API_BASE_URL}/stats/players/${playerId}/competitions/${competitionDbId}/active-season/stat-composition`;
  console.log(`[API Stats] Fetching stat composition for P:${playerId}, CompDB:${competitionDbId}`);
  
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await statsApiClient.get<PlayerStatCompositionResponse>(url, { headers });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch stat composition for player ${playerId}, competition ${competitionDbId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error.message) {
      errorMessage = error.message;
    }
    console.error(`[API Stats] Error fetching stat composition for P:${playerId} CompDB:${competitionDbId}:`, errorMessage, error);
    throw new ApiError(errorMessage, status);
  }
};
// <<< --- END NEW API FUNCTION --- >>>

// --- NEW API FUNCTION for Player-Match Score Breakdown ---
export const fetchPlayerMatchScoreBreakdown = async (
  playerId: number,
  matchId: number,
  token: string | null, // Keep token for potential future auth
  signal?: AbortSignal,
): Promise<schemas.ApiFantasyScore[]> => { // Use the existing ApiFantasyScore schema
  if (!playerId || !matchId) {
    throw new ApiError("Player ID and Match ID are required.", 400);
  }
  const url = `${API_BASE_URL}/stats/matches/${matchId}/players/${playerId}/score-breakdown`;
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  
  console.log(`[API Stats] Fetching score breakdown for P:${playerId}, M:${matchId}`);

  try {
    const response = await statsApiClient.get<schemas.ApiFantasyScore[]>(url, { headers, signal });
    return response.data;
  } catch (error: unknown) {
    // A caller-aborted request (modal closed) is not a failure.
    if (isCancel(error)) throw error;
    let errorMessage = `Failed to fetch score breakdown for player ${playerId} in match ${matchId}.`;
    let status: number | undefined;
    if (isAxiosError(error)) {
      status = error.response?.status;
      errorMessage = error.response?.data?.detail || errorMessage;
    } else if (error instanceof Error && error.message) {
      errorMessage = error.message;
    }
    console.error(`[API Stats] Error fetching score breakdown for P:${playerId} M:${matchId}:`, errorMessage, error);
    throw new ApiError(errorMessage, status, { cause: error });
  }
};
// --- END NEW API FUNCTION ---

// Scoring moments with pitch replay clips for the score breakdown modal.
// Login required and rate limited server-side.
export const fetchPlayerMatchMoments = async (
  playerId: number,
  matchId: number,
  token: string,
  signal?: AbortSignal,
): Promise<PlayerMatchMoments> => {
  try {
    const response = await statsApiClient.get<PlayerMatchMoments>(
      `${API_BASE_URL}/stats/matches/${matchId}/players/${playerId}/moments`,
      { headers: { Authorization: `Bearer ${token}` }, signal },
    );
    return response.data;
  } catch (error: unknown) {
    // A caller-aborted request (modal closed) is not a failure.
    if (isCancel(error)) throw error;
    const status = isAxiosError(error) ? error.response?.status : undefined;
    throw new ApiError(`Failed to fetch moments for player ${playerId} in match ${matchId}.`, status, { cause: error });
  }
};

// --- GAME LOG LEADERBOARD ---
export const fetchGameLogLeaderboard = async (
  competitionDbId: number,
  filters: {
    sort_by?: string;
    order?: 'asc' | 'desc';
    skip?: number;
    limit?: number;
    position?: string | null;
    search?: string | null;
    date_from?: string | null;
    date_to?: string | null;
  },
  token?: string | null
): Promise<GameLogLeaderboardEntry[]> => {
  if (!competitionDbId) throw new Error("Competition DB ID is required.");

  const queryParams = new URLSearchParams();
  if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
  if (filters.order) queryParams.append('order', filters.order);
  if (filters.skip !== null && filters.skip !== undefined) queryParams.append('skip', String(filters.skip));
  if (filters.limit !== null && filters.limit !== undefined) queryParams.append('limit', String(filters.limit));
  if (filters.position) queryParams.append('position', filters.position);
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.date_from) queryParams.append('date_from', filters.date_from);
  if (filters.date_to) queryParams.append('date_to', filters.date_to);

  const url = `${API_BASE_URL}/stats/competitions/${competitionDbId}/active-season/game-logs?${queryParams.toString()}`;
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await statsApiClient.get<GameLogLeaderboardEntry[]>(url, { headers });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch game log leaderboard for competition ${competitionDbId}.`;
    throw new ApiError(errorMessage, error?.response?.status);
  }
};

// --- CONTEST GAME LOGS (scoped to a specific DFS contest) ---
export const fetchContestGameLogs = async (
  contestId: number,
  filters: {
    sort_by?: string;
    order?: 'asc' | 'desc';
    skip?: number;
    limit?: number;
    position?: string | null;
    search?: string | null;
  },
  token?: string | null
): Promise<GameLogLeaderboardEntry[]> => {
  if (!contestId) throw new Error("Contest ID is required.");

  const queryParams = new URLSearchParams();
  if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
  if (filters.order) queryParams.append('order', filters.order);
  if (filters.skip !== null && filters.skip !== undefined) queryParams.append('skip', String(filters.skip));
  if (filters.limit !== null && filters.limit !== undefined) queryParams.append('limit', String(filters.limit));
  if (filters.position) queryParams.append('position', filters.position);
  if (filters.search) queryParams.append('search', filters.search);

  const url = `${API_BASE_URL}/dfs/contests/${contestId}/game-logs?${queryParams.toString()}`;
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await statsApiClient.get<GameLogLeaderboardEntry[]>(url, { headers });
    return response.data;
  } catch (error: any) {
    const errorMessage = `Failed to fetch game logs for contest ${contestId}.`;
    throw new ApiError(errorMessage, error?.response?.status);
  }
};

// --- HISTORICAL SEASON STATS API FUNCTIONS ---

export const fetchAvailableHistoricalSeasons = async (
  token?: string | null
): Promise<AvailableHistoricalSeason[]> => {
  const url = `${API_BASE_URL}/stats/historical/seasons`;
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await statsApiClient.get<AvailableHistoricalSeason[]>(url, { headers });
    return response.data;
  } catch (error: any) {
    let errorMessage = 'Failed to fetch historical seasons.';
    throw new ApiError(errorMessage, error?.response?.status);
  }
};

export const fetchResearchSeasons = async (
  competitionDbId: number,
  playerId?: number,
  pinCompetition?: boolean,
): Promise<ResearchSeasonOption[]> => {
  // playerId makes the server resolve the PLAYER'S own competition (combined
  // leagues pass their anchor comp for everyone) - read competition_id off
  // the returned rows rather than trusting competitionDbId.
  // pinCompetition disables that inference: historical/stats pages pass the
  // competition the user is actually browsing, so a transferred player must
  // NOT jump to his new league's seasons (review catch 2026-07-22).
  const params = new URLSearchParams();
  if (playerId) params.set('player_id', String(playerId));
  if (pinCompetition) params.set('pin_competition', 'true');
  const qs = params.toString();
  const url = `${API_BASE_URL}/stats/competitions/${competitionDbId}/research-seasons${qs ? `?${qs}` : ''}`;
  try {
    const response = await statsApiClient.get<ResearchSeasonOption[]>(url);
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to fetch research seasons.', error?.response?.status);
  }
};

export const fetchPlayerSeasonSummary = async (
  playerId: number,
  competitionDbId: number,
  seasonIdentifier: string,
  token?: string | null,
): Promise<PlayerSeasonSummary | null> => {
  const url = `${API_BASE_URL}/stats/players/${playerId}/competitions/${competitionDbId}/season-summary?season_identifier=${encodeURIComponent(seasonIdentifier)}`;
  const headers: { [key: string]: string } = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    const response = await statsApiClient.get<PlayerSeasonSummary>(url, { headers });
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    throw new ApiError(`Failed to fetch ${seasonIdentifier} statistics.`, error?.response?.status);
  }
};

export const fetchHistoricalPlayerStats = async (
  competitionDbId: number,
  seasonIdentifier: string,
  filters: {
    position?: string | null;
    search?: string | null;
    sort_by?: string;
    order?: 'asc' | 'desc';
    skip?: number;
    limit?: number;
  },
  token?: string | null
): Promise<PlayerStatsEntryForList[]> => {
  if (!competitionDbId || !seasonIdentifier) throw new Error("Competition ID and season identifier are required.");

  const queryParams = new URLSearchParams();
  if (filters.position) queryParams.append('position', filters.position);
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.sort_by) queryParams.append('sort_by', filters.sort_by);
  if (filters.order) queryParams.append('order', filters.order);
  if (filters.skip !== null && filters.skip !== undefined) queryParams.append('skip', String(filters.skip));
  if (filters.limit !== null && filters.limit !== undefined) queryParams.append('limit', String(filters.limit));

  // season_identifier travels as a query param: identifiers contain a literal
  // slash ("La Liga 2025/2026") which cannot survive in a URL path segment.
  queryParams.append('season_identifier', seasonIdentifier);
  const url = `${API_BASE_URL}/stats/historical/competitions/${competitionDbId}/players?${queryParams.toString()}`;
  const headers: { [key: string]: string } = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await statsApiClient.get<PlayerStatsEntryForList[]>(url, { headers });
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch historical stats for ${seasonIdentifier}.`;
    throw new ApiError(errorMessage, error?.response?.status);
  }
};
// --- END HISTORICAL SEASON STATS API FUNCTIONS ---

// --- MESSI CAREER STATS API FUNCTIONS ---
export interface MessiSeasonSummary {
  id: number;
  season_name: string;
  competition_name: string;
  team_name: string;
  games: number;
  goals: number;
  shots: number;
  shots_on_target: number;
  sca: number;
  gca: number;
  prog_passes: number;
  prog_carries: number;
  dispossessed: number;
  fantasy_points: number;
  fppg: number;
}

export interface MessiGameLogEntry {
  id: number;
  season_name: string;
  match_date: string | null;
  opponent: string | null;
  home_away: string | null;
  goals: number;
  shots: number;
  shots_on_target: number;
  sca: number;
  gca: number;
  prog_passes: number;
  prog_carries: number;
  dispossessed: number;
  fantasy_points: number;
}

export const fetchMessiSeasons = async (
  sortBy: string = 'fantasy_points',
  order: string = 'desc',
): Promise<MessiSeasonSummary[]> => {
  const url = `${API_BASE_URL}/messi/seasons?sort_by=${sortBy}&order=${order}`;
  try {
    const response = await statsApiClient.get<MessiSeasonSummary[]>(url);
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to fetch Messi season stats.', error?.response?.status);
  }
};

export const fetchMessiGameLog = async (
  seasonName: string,
  sortBy: string = 'match_date',
  order: string = 'desc',
): Promise<MessiGameLogEntry[]> => {
  const url = `${API_BASE_URL}/messi/seasons/${encodeURIComponent(seasonName)}/game-log?sort_by=${sortBy}&order=${order}`;
  try {
    const response = await statsApiClient.get<MessiGameLogEntry[]>(url);
    return response.data;
  } catch (error: any) {
    throw new ApiError('Failed to fetch Messi game log.', error?.response?.status);
  }
};
// --- END MESSI CAREER STATS API FUNCTIONS ---
